import { memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Brain, CheckCircle2, AlertCircle, InfoIcon, Clock } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
}

const Skeleton = memo(({ className = '', width, height }: SkeletonProps) => (
  <div
    className={`bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-pulse rounded ${className}`}
    style={{
      width: width || '100%',
      height: height || '1rem',
      animation: 'shimmer 2s infinite linear',
    }}
  />
));

Skeleton.displayName = 'Skeleton';

// Memory Card Skeleton
export const MemoryCardSkeleton = memo(() => (
  <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
    <div className="flex items-start justify-between">
      <div className="flex-1 space-y-2">
        <Skeleton height="20px" width="75%" />
        <Skeleton height="16px" width="90%" />
        <Skeleton height="16px" width="60%" />
      </div>
      <Skeleton width="24px" height="24px" className="rounded-full" />
    </div>
    
    <div className="flex items-center space-x-2">
      <Skeleton width="60px" height="20px" className="rounded-full" />
      <Skeleton width="80px" height="20px" className="rounded-full" />
      <Skeleton width="70px" height="20px" className="rounded-full" />
    </div>
    
    <div className="flex items-center justify-between">
      <Skeleton width="120px" height="14px" />
      <Skeleton width="80px" height="14px" />
    </div>
  </div>
));

MemoryCardSkeleton.displayName = 'MemoryCardSkeleton';

// Socket Card Skeleton
export const SocketCardSkeleton = memo(() => (
  <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
    <div className="flex items-start justify-between">
      <div className="flex items-center space-x-3">
        <Skeleton width="40px" height="40px" className="rounded-lg" />
        <div className="space-y-2">
          <Skeleton width="140px" height="20px" />
          <div className="flex items-center space-x-2">
            <Skeleton width="100px" height="16px" className="rounded-full" />
            <Skeleton width="60px" height="16px" className="rounded-full" />
          </div>
        </div>
      </div>
      <div className="flex space-x-1">
        <Skeleton width="32px" height="32px" className="rounded" />
        <Skeleton width="32px" height="32px" className="rounded" />
        <Skeleton width="32px" height="32px" className="rounded" />
      </div>
    </div>
    
    <Skeleton height="16px" width="85%" />
    
    <div className="flex items-center justify-between pt-2">
      <Skeleton width="100px" height="14px" />
      <Skeleton width="80px" height="28px" className="rounded" />
    </div>
  </div>
));

SocketCardSkeleton.displayName = 'SocketCardSkeleton';

// Space Card Skeleton
export const SpaceCardSkeleton = memo(() => (
  <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
    <div className="flex items-center space-x-3">
      <Skeleton width="48px" height="48px" className="rounded-lg" />
      <div className="flex-1 space-y-2">
        <Skeleton width="120px" height="20px" />
        <Skeleton width="180px" height="16px" />
      </div>
    </div>
    
    <div className="flex items-center justify-between">
      <Skeleton width="80px" height="14px" />
      <Skeleton width="60px" height="20px" className="rounded-full" />
    </div>
  </div>
));

SpaceCardSkeleton.displayName = 'SpaceCardSkeleton';

// Full Page Loading
interface FullPageLoadingProps {
  message?: string;
  subtitle?: string;
  status?: 'loading' | 'success' | 'error' | 'info' | 'warning';
  showProgress?: boolean;
  progressValue?: number;
  realm?: 'outer' | 'sanctuary';
}

export const FullPageLoading = memo(({ 
  message = 'Loading...', 
  subtitle,
  status = 'loading',
  showProgress = false,
  progressValue = 0,
  realm = 'outer'
}: FullPageLoadingProps) => {
  // Status icon mapping
  const StatusIcon = {
    loading: Brain,
    success: CheckCircle2,
    error: AlertCircle,
    info: InfoIcon,
    warning: Clock
  }[status];
  
  // Status color mapping
  const statusColors = {
    loading: {
      bg: 'from-indigo-500 to-purple-600',
      icon: 'text-white',
      dots: 'bg-indigo-400'
    },
    success: {
      bg: 'from-emerald-500 to-green-600',
      icon: 'text-white',
      dots: 'bg-emerald-400'
    },
    error: {
      bg: 'from-red-500 to-rose-600',
      icon: 'text-white',
      dots: 'bg-red-400'
    },
    info: {
      bg: 'from-blue-500 to-sky-600',
      icon: 'text-white',
      dots: 'bg-blue-400'
    },
    warning: {
      bg: 'from-amber-500 to-yellow-600',
      icon: 'text-white',
      dots: 'bg-amber-400'
    }
  };
  
  const colors = statusColors[status];
  const realmStyles = realm === 'sanctuary' ? 
    'bg-gradient-to-br from-purple-900 to-indigo-800 text-white' : 
    'bg-gradient-to-br from-indigo-50 via-white to-purple-50';
  
  return (
    <div className={`min-h-screen ${realmStyles} flex items-center justify-center`}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="text-center space-y-6 max-w-md px-4"
      >
        <motion.div
          animate={status === 'loading' ? { rotate: 360 } : { scale: [0.95, 1.05, 0.95] }}
          transition={status === 'loading' ? 
            { duration: 2, repeat: Infinity, ease: "linear" } : 
            { duration: 3, repeat: Infinity, ease: "easeInOut" }
          }
          className={`w-16 h-16 mx-auto bg-gradient-to-br ${colors.bg} rounded-2xl flex items-center justify-center shadow-lg`}
        >
          <StatusIcon className={`w-8 h-8 ${colors.icon}`} />
        </motion.div>
        
        <div>
          <h3 className={cn(
            "text-xl font-semibold", 
            realm === 'sanctuary' ? "text-white" : "text-gray-900"
          )}>
            {message}
          </h3>
          {subtitle && (
            <p className={cn(
              "text-md mt-2",
              realm === 'sanctuary' ? "text-white/80" : "text-gray-600"
            )}>
              {subtitle}
            </p>
          )}
        </div>
        
        {showProgress && (
          <div className="w-full space-y-2 px-4">
            <Progress value={progressValue} className="w-full h-2" />
            <p className={cn(
              "text-sm",
              realm === 'sanctuary' ? "text-white/70" : "text-gray-500"
            )}>
              {Math.round(progressValue)}% Complete
            </p>
          </div>
        )}
        
        {status === 'loading' && !showProgress && (
          <div className="flex justify-center space-x-2">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: i * 0.2,
                }}
                className={`w-2 h-2 ${colors.dots} rounded-full`}
              />
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
});

FullPageLoading.displayName = 'FullPageLoading';

// Inline Loading Spinner
interface InlineLoadingProps {
  size?: 'xs' | 'sm' | 'md' | 'lg';
  message?: string;
  className?: string;
  status?: 'loading' | 'success' | 'error' | 'info' | 'warning';
  variant?: 'default' | 'subtle' | 'branded';
}

export const InlineLoading = memo(({ 
  size = 'md', 
  message = 'Loading...', 
  className = '',
  status = 'loading',
  variant = 'default'
}: InlineLoadingProps) => {
  const sizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };
  
  const textSizes = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg'
  };
  
  // Status icon mapping
  const StatusIcon = {
    loading: Loader2,
    success: CheckCircle2,
    error: AlertCircle,
    info: InfoIcon,
    warning: Clock
  }[status];
  
  // Status color mapping based on variant
  const statusColors = {
    loading: {
      default: 'text-indigo-600',
      subtle: 'text-indigo-400',
      branded: 'text-purple-500'
    },
    success: {
      default: 'text-emerald-600',
      subtle: 'text-emerald-400',
      branded: 'text-emerald-500'
    },
    error: {
      default: 'text-red-600',
      subtle: 'text-red-400',
      branded: 'text-red-500'
    },
    info: {
      default: 'text-blue-600',
      subtle: 'text-blue-400',
      branded: 'text-blue-500'
    },
    warning: {
      default: 'text-amber-600',
      subtle: 'text-amber-400',
      branded: 'text-amber-500'
    }
  };
  
  const textColors = {
    default: 'text-gray-600',
    subtle: 'text-gray-500',
    branded: 'text-gray-700'
  };
  
  const bgColors = {
    default: '',
    subtle: '',
    branded: status === 'loading' ? 'bg-indigo-50 border border-indigo-100 rounded-full px-3 py-1' : 
             status === 'success' ? 'bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1' : 
             status === 'error' ? 'bg-red-50 border border-red-100 rounded-full px-3 py-1' : 
             status === 'info' ? 'bg-blue-50 border border-blue-100 rounded-full px-3 py-1' : 
             'bg-amber-50 border border-amber-100 rounded-full px-3 py-1'
  };

  return (
    <div className={cn(
      `flex items-center justify-center space-x-2`, 
      bgColors[variant],
      className
    )}>
      <AnimatePresence mode="wait">
        <motion.div
          key={status}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.2 }}
        >
          {status === 'loading' ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            >
              <StatusIcon className={`${statusColors[status][variant]} ${sizeClasses[size]}`} />
            </motion.div>
          ) : (
            <StatusIcon className={`${statusColors[status][variant]} ${sizeClasses[size]}`} />
          )}
        </motion.div>
      </AnimatePresence>
      <span className={`${textColors[variant]} ${textSizes[size]}`}>{message}</span>
    </div>
  );
});

InlineLoading.displayName = 'InlineLoading';

// Progress Loading for Operations
interface ProgressLoadingProps {
  progress: number;
  message?: string;
  subtitle?: string;
  className?: string;
}

export const ProgressLoading = memo(({ 
  progress, 
  message = 'Processing...', 
  subtitle,
  className = '' 
}: ProgressLoadingProps) => (
  <div className={`space-y-4 ${className}`}>
    <div className="text-center">
      <h3 className="text-lg font-semibold text-gray-900">{message}</h3>
      {subtitle && (
        <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
      )}
    </div>
    
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-gray-600">Progress</span>
        <span className="font-medium text-gray-900">{Math.round(progress)}%</span>
      </div>
      <Progress value={progress} className="w-full" />
    </div>
    
    <div className="flex justify-center">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        className="w-6 h-6 text-indigo-600"
      >
        <Loader2 className="w-6 h-6" />
      </motion.div>
    </div>
  </div>
));

ProgressLoading.displayName = 'ProgressLoading';

// Enhanced Creation Loading with Steps
interface CreationLoadingProps {
  steps: string[];
  currentStep: number;
  message?: string;
  className?: string;
}

export const CreationLoading = memo(({ 
  steps, 
  currentStep, 
  message = 'Creating...',
  className = '' 
}: CreationLoadingProps) => {
  const progress = ((currentStep + 1) / steps.length) * 100;
  
  return (
    <div className={`space-y-6 ${className}`}>
      <div className="text-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 mx-auto bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-4"
        >
          <Brain className="w-6 h-6 text-white" />
        </motion.div>
        
        <h3 className="text-lg font-semibold text-gray-900">{message}</h3>
        <p className="text-sm text-gray-600 mt-1">
          Step {currentStep + 1} of {steps.length}
        </p>
      </div>
      
      <div className="space-y-4">
        <Progress value={progress} className="w-full" />
        
        <div className="space-y-2">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0.3 }}
              animate={{ 
                opacity: index <= currentStep ? 1 : 0.3,
                scale: index === currentStep ? 1.02 : 1
              }}
              transition={{ duration: 0.3 }}
              className={`flex items-center space-x-3 p-2 rounded-lg ${
                index === currentStep 
                  ? 'bg-indigo-50 border border-indigo-200' 
                  : index < currentStep 
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-gray-50 border border-gray-200'
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${
                index < currentStep 
                  ? 'bg-green-500 text-white' 
                  : index === currentStep 
                    ? 'bg-indigo-500 text-white'
                    : 'bg-gray-300 text-gray-600'
              }`}>
                {index < currentStep ? '✓' : index + 1}
              </div>
              <span className={`text-sm ${
                index <= currentStep ? 'text-gray-900 font-medium' : 'text-gray-500'
              }`}>
                {step}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
});

CreationLoading.displayName = 'CreationLoading';

// List Loading with Skeletons
interface ListLoadingProps {
  count?: number;
  type: 'memory' | 'socket' | 'space';
}

export const ListLoading = memo(({ count = 3, type }: ListLoadingProps) => {
  const SkeletonComponent = {
    memory: MemoryCardSkeleton,
    socket: SocketCardSkeleton,
    space: SpaceCardSkeleton,
  }[type];

  return (
    <div className="space-y-4">
      {Array.from({ length: count }, (_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.1 }}
        >
          <SkeletonComponent />
        </motion.div>
      ))}
    </div>
  );
});

ListLoading.displayName = 'ListLoading';

// Portal Transition Loading (Enhanced version with direction)
export const PortalLoading = memo(({ 
  direction = 'entering',
  message,
  showProgress = true
}: {
  direction?: 'entering' | 'exiting'; 
  message?: string;
  showProgress?: boolean;
}) => {
  const isEntering = direction === 'entering';
  const defaultMessage = isEntering ? 'Entering Memory Workspace' : 'Returning to Dashboard';
  const displayMessage = message || defaultMessage;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-gradient-to-br from-purple-900/80 to-indigo-900/80 backdrop-blur-md flex items-center justify-center"
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 500 }}
        className="bg-white/10 backdrop-blur-md rounded-2xl p-10 shadow-2xl border border-white/10 max-w-md"
      >
        <div className="text-center space-y-6">
          {/* Rotating portal effect */}
          <div className="relative mx-auto w-24 h-24">
            <motion.div
              animate={{ rotate: 360, opacity: [0.7, 0.9, 0.7] }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 w-full h-full border-2 border-purple-300/40 rounded-full"
            />
            
            <motion.div
              animate={{ rotate: -180, opacity: [0.5, 0.8, 0.5] }}
              transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 w-20 h-20 m-2 border-2 border-indigo-400/60 rounded-full"
            />
            
            <motion.div
              animate={{ 
                rotate: isEntering ? 360 : -360,
                scale: [1, 1.05, 1]
              }}
              transition={{ 
                rotate: { duration: 4, repeat: Infinity, ease: "linear" },
                scale: { duration: 2, repeat: Infinity, ease: "easeInOut" }
              }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                <Brain className="w-8 h-8 text-white" />
              </div>
            </motion.div>
            
            {/* Particles */}
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1.5 h-1.5 bg-purple-300/80 rounded-full"
                style={{
                  left: `${50 + 45 * Math.cos(i * Math.PI / 3)}%`,
                  top: `${50 + 45 * Math.sin(i * Math.PI / 3)}%`,
                }}
                animate={{
                  scale: [0.8, 1.5, 0.8],
                  opacity: [0.4, 1, 0.4],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  delay: i * 0.5,
                }}
              />
            ))}
          </div>
          
          <div>
            <h3 className="text-xl font-bold mb-3 text-white font-workspace tracking-wide">
              {displayMessage}
            </h3>
            <p className="text-md text-white/80">
              {isEntering ? 
                'Your memory workspace is opening...' : 
                'Returning to your dashboard...'}
            </p>
          </div>
          
          {showProgress && (
            <div className="w-full space-y-2 px-4">
              <div className="h-1 bg-white/20 rounded-full w-full overflow-hidden">
                <motion.div 
                  className="h-full bg-white/80"
                  initial={{ width: '0%' }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 2.5, ease: "easeInOut" }}
                />
              </div>
            </div>
          )}
          
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-sm text-white/60 italic"
          >
            {isEntering ? 
              "Loading memory management tools..." : 
              "Returning to account dashboard..."}
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
});

PortalLoading.displayName = 'PortalLoading'; 